import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  Modal,
  Row,
  Select
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DefaultOptionType } from 'antd/es/select'
import { ModalProps } from 'antd/lib'
import { Dispatch, SetStateAction } from 'react'

import {
  ClassifierCategory,
  ClassifierType,
  InvestigationTestTypeCategory
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { ClassifierInfiniteSelect } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

import { ReferenceIntervalInfiniteSelect } from './ReferenceIntervalInfiniteSelect'
import { ValueOptionInfiniteSelect } from './ValueOptionInfiniteSelect'

type Props = {
  onCancel: () => void
  setTestTypes: Dispatch<SetStateAction<AnyObject[]>>
  form: FormInstance
  currentTestIndex?: number
} & ModalProps

export const TestTypeModal = ({
  onCancel,
  setTestTypes,
  form,
  currentTestIndex,
  ...props
}: Props) => {
  const { t } = useLocales()

  const classifierResultTypeId = useClassifierId(
    ClassifierType.INVESTIGATION_RESULT_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )
  const classifierUnitMeasureId = useClassifierId(
    ClassifierType.INVESTIGATION_UNIT_MEASURE,
    ClassifierCategory.INVESTIGATIONS
  )
  const classifierResultOptionId = useClassifierId(
    ClassifierType.INVESTIGATION_RESULT_OPTION,
    ClassifierCategory.INVESTIGATIONS
  )
  const infectionIdClassifierId = useClassifierId(
    ClassifierType.INFECTION,
    ClassifierCategory.INVESTIGATIONS
  )

  const testTypeCategoryOptions = Object.values(
    InvestigationTestTypeCategory
  ).map((item) => ({
    value: item,
    label: t(`laboratory_classifiers.test_type_category_options.${item}`)
  }))

  const onSave = async () => {
    await form.validateFields([
      'infectionId',
      'resultTypeId',
      'resultUnitMeasureId',
      'resultReferenceIntervalIds',
      'resultOptions'
    ])

    const allValues = form.getFieldsValue(true)

    const payload = {
      infection: allValues.infection,
      infectionId: allValues.infectionId,
      resultTypeId: allValues.resultTypeId,
      resultUnitMeasureId: allValues.resultUnitMeasureId,
      resultReferenceIntervalIds: allValues.resultReferenceIntervalIds,
      type: allValues.type ?? null,
      resultOptions: allValues.resultOptions?.map(
        (item: { id: string; valueOptionIds: string[] }) => ({
          id: item.id,
          valueOptionIds: item.valueOptionIds
        })
      )
    }

    if (currentTestIndex !== undefined) {
      setTestTypes((prev) => {
        const newTestTypes = [...prev]
        // Keep the edited row's id (the shared form's `id` may be the investigation type's),
        // so the backend updates the test type in place; new rows have none.
        newTestTypes[currentTestIndex] = {
          ...payload,
          id: prev[currentTestIndex]?.id
        }
        return newTestTypes
      })
    } else {
      setTestTypes((prev) => [...prev, payload])
    }

    onCancel()
  }

  return (
    <Modal
      open
      centered
      width={800}
      maskClosable={false}
      onCancel={onCancel}
      title={t('laboratory_classifiers.add_test_type')}
      footer={null}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          {infectionIdClassifierId && (
            <Col span={12}>
              <Form.Item
                name="infectionId"
                label={t('laboratory_classifiers.investigation_infection')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={infectionIdClassifierId}
                  onChange={(_, { label, value }: DefaultOptionType) => {
                    form.setFieldValue('infectionId', value)
                    form.setFieldValue('infection', label)
                  }}
                  placeholder={t(
                    'laboratory_classifiers.investigation_infection'
                  )}
                />
              </Form.Item>
            </Col>
          )}

          {classifierResultTypeId && (
            <Col span={12}>
              <Form.Item
                name="resultTypeId"
                rules={requiredRules}
                label={t('laboratory_classifiers.result_type')}
              >
                <ClassifierInfiniteSelect
                  placeholder={t('common.select_from_list')}
                  id={classifierResultTypeId}
                />
              </Form.Item>
            </Col>
          )}

          {classifierUnitMeasureId && (
            <Col span={12}>
              <Form.Item
                name="resultUnitMeasureId"
                label={t('laboratory_classifiers.unit_measure')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  placeholder={t('common.select_from_list')}
                  id={classifierUnitMeasureId}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item
              name="resultReferenceIntervalIds"
              label={t('laboratory_classifiers.reference_interval_result')}
              rules={requiredRules}
            >
              <ReferenceIntervalInfiniteSelect mode="multiple" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="type"
              label={t('laboratory_classifiers.test_type_category')}
            >
              <Select
                allowClear
                options={testTypeCategoryOptions}
                placeholder={t('common.select_from_list')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Card title={t('laboratory_classifiers.result_options')}>
              <Form.List name="resultOptions">
                {(fields, { add, remove }) => (
                  <Flex vertical gap={16}>
                    {fields.map(({ key, name }) => {
                      return (
                        <Row key={key} gutter={24} align="bottom">
                          <Col span={11}>
                            <Form.Item
                              name={[name, 'id']}
                              label={t('laboratory_classifiers.result_options')}
                            >
                              <ClassifierInfiniteSelect
                                placeholder={t('common.select_from_list')}
                                id={classifierResultOptionId}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={11}>
                            <Form.Item
                              name={[name, 'valueOptionIds']}
                              label={t(
                                'laboratory_classifiers.value_option_result'
                              )}
                            >
                              <ValueOptionInfiniteSelect mode="multiple" />
                            </Form.Item>
                          </Col>

                          <Col span={2}>
                            <Button
                              onClick={() => remove(name)}
                              icon={<DeleteOutlined />}
                            />
                          </Col>
                        </Row>
                      )
                    })}

                    <Button onClick={add} icon={<PlusOutlined />} block>
                      {t('common.add_option')}
                    </Button>
                  </Flex>
                )}
              </Form.List>
            </Card>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <Button onClick={onSave} type="primary">
                {t('common.save')}
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
